import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';
import type { SessionUser } from '@details-ai/contracts';
import { api, setCsrfToken } from '../lib/api';

interface LoginResponse {
  requiresOtp: boolean;
  challengeId?: string;
  maskedDestination?: string;
  csrfToken?: string;
  user?: SessionUser;
}

interface AuthValue {
  user: SessionUser | null;
  loading: boolean;
  challenge: { id: string; destination: string } | null;
  login: (username: string, password: string) => Promise<LoginResponse>;
  verifyOtp: (code: string) => Promise<void>;
  resendOtp: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState<AuthValue['challenge']>(null);

  useEffect(() => {
    let active = true;
    api<{ user: SessionUser; csrfToken: string }>('/api/auth/me', {
      skipRefresh: false,
    })
      .then((data) => {
        if (!active) return;
        setCsrfToken(data.csrfToken);
        setUser(data.user);
      })
      .catch(() => {
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function login(username: string, password: string) {
    const result = await api<LoginResponse>('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      skipRefresh: true,
    });
    if (result.requiresOtp && result.challengeId) {
      setChallenge({
        id: result.challengeId,
        destination: result.maskedDestination || 'قناة التحقق',
      });
    } else if (result.user && result.csrfToken) {
      setCsrfToken(result.csrfToken);
      setUser(result.user);
    }
    return result;
  }

  async function verifyOtp(code: string) {
    if (!challenge) throw new Error('لا يوجد طلب تحقق نشط');
    const result = await api<LoginResponse>('/api/admin/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ challengeId: challenge.id, code }),
      skipRefresh: true,
    });
    if (!result.user || !result.csrfToken) throw new Error('تعذر إنشاء الجلسة');
    setCsrfToken(result.csrfToken);
    setUser(result.user);
    setChallenge(null);
  }

  async function resendOtp() {
    if (!challenge) throw new Error('لا يوجد طلب تحقق نشط');
    const result = await api<{ challengeId: string; maskedDestination: string }>(
      '/api/admin/auth/otp/resend',
      {
        method: 'POST',
        body: JSON.stringify({ challengeId: challenge.id }),
        skipRefresh: true,
      },
    );
    setChallenge({ id: result.challengeId, destination: result.maskedDestination });
  }

  async function logout() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    setCsrfToken(null);
    setUser(null);
    setChallenge(null);
  }

  return (
    <AuthContext.Provider
      value={{ user, loading, challenge, login, verifyOtp, resendOtp, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
